import * as React from 'react';

import {
  enumDriverSortOption,
  enumDriverStatus,
  DriverStatus,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  Badge,
  BadgeProps,
  Button,
  Content,
  DataGrid,
  DataGridColumn,
  FloatingToolbar,
  Footer,
  HeaderIcons,
  Loading,
  Pagination,
  SearchField,
  Separator,
  Toolbar,
  type PaginationProps,
  useDataGridSelection,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';
import { PlusCircle, Trash } from 'lucide-react';

import { DriverDialog } from './DriverDialog';

import {
  $limit,
  $offset,
  $searchTerm,
} from '~/modules/transportation/_state/driversList.state';
import { useDeleteDriverMutation } from '~/modules/transportation/hooks/mutations/useDeleteDriverMutation';
import { useDriversQuery } from '~/modules/transportation/hooks/queries/useDriversQuery';
import { TransportationRootLayout } from '~/modules/transportation/layouts/TransportationRootLayout';
import { DriverRecord } from '~/modules/transportation/types';
import useDebouncedValue from '~/shared/hooks/useDebounce';
import { $localization } from '~/shared/state/preferences.state';

const getStatusColor = (status: DriverStatus): BadgeProps['color'] => {
  switch (status) {
    case enumDriverStatus.Available:
      return 'success';
    case enumDriverStatus.OnDuty:
      return 'purple';
    case enumDriverStatus.OffDuty:
      return 'neutral';
    case enumDriverStatus.OnBreak:
      return 'warning';
    case enumDriverStatus.Inactive:
      return 'failure';
    default:
      return 'neutral';
  }
};

export const DriversListView = () => {
  const t = useTranslations();
  const [selectedRows, setSelectedRows] = React.useState<DriverRecord[]>([]);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editDriver, setEditDriver] = React.useState<DriverRecord | null>(null);
  const localization = useAtomValue($localization);
  const [searchTerm, setSearchTerm] = useAtom($searchTerm);
  const [offset, setOffset] = useAtom($offset);
  const [limit, setLimit] = useAtom($limit);
  const toast = useToast();

  const debouncedSearchTerm = useDebouncedValue(searchTerm ?? '', 500);

  const { data, error, isLoading, refetch } = useDriversQuery({
    searchTerm: debouncedSearchTerm || undefined,
    limit,
    offset,
    sorting: [enumDriverSortOption.CreatedAtDesc],
  });

  const { mutateAsync: deleteDriver } = useDeleteDriverMutation();

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value || undefined);
    setOffset(0);
  };

  const handleCreate = () => {
    setEditDriver(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await Promise.all(
        selectedRows.map((row) => deleteDriver({ id: row.id })),
      );
      toast.success(
        t.driversView.messages.deleteSuccess({
          count: selectedRows.length,
        }),
      );
      setSelectedRows([]);
      refetch();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.driversView.messages.error,
      );
    }
  };

  const COLUMNS: DataGridColumn<DriverRecord>[] = [
    {
      key: 'status',
      header: t.driversView.table.columns.status,
      icon: HeaderIcons.Tag,
      width: 150,
      resizable: true,
      sortable: true,
      cell: ({ row }) => (
        <Badge color={getStatusColor(row.status)} size="small">
          {t.driversView.status[row.status]}
        </Badge>
      ),
    },
    {
      key: 'name',
      header: t.driversView.table.columns.name,
      icon: HeaderIcons.Text,
      width: 200,
      resizable: true,
      sortable: true,
      cell: ({ row }) => `${row.firstName} ${row.lastName}`,
    },
    {
      key: 'licenseNumber',
      header: t.driversView.table.columns.licenseNumber,
      icon: HeaderIcons.Text,
      width: 150,
      resizable: true,
      cell: ({ row }) => row.licenseNumber || '-',
    },
    {
      key: 'licenseExpiration',
      header: t.driversView.table.columns.licenseExpiration,
      icon: HeaderIcons.Date,
      width: 150,
      resizable: true,
      sortable: true,
      cell: ({ row }) =>
        row.licenseExpiration
          ?.toDate?.(localization.timezone)
          .toLocaleDateString?.() ?? '-',
    },
    {
      key: 'phone',
      header: t.driversView.table.columns.phone,
      icon: HeaderIcons.Phone,
      width: 150,
      resizable: true,
      cell: ({ row }) => row.phoneNumber,
    },
    {
      key: 'email',
      header: t.driversView.table.columns.email,
      icon: HeaderIcons.Email,
      width: 200,
      resizable: true,
      cell: ({ row }) => row.email,
    },
    {
      key: 'address',
      header: t.driversView.table.columns.address,
      icon: HeaderIcons.Address,
      grow: 1,
      resizable: true,
      cell: ({ row }) =>
        [row.address.city, row.address.state, row.address.country]
          .filter((a) => a !== '')
          .join(', '),
    },
  ];

  const drivers: DriverRecord[] = data?.drivers ?? [];
  const handleSelectionChange = useDataGridSelection(drivers, setSelectedRows);

  if (error) {
    return (
      <TransportationRootLayout>
        <div className="p-4 text-failure">
          {t.driversView.content.error(error.message)}
        </div>
      </TransportationRootLayout>
    );
  }

  return (
    <TransportationRootLayout className="@DriversListView">
      <Toolbar>
        <div className="flex items-center gap-4">
          <SearchField
            value={searchTerm ?? ''}
            onChange={handleSearch}
            placeholder={t.driversView.toolbar.search}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            onPress={handleCreate}
            variant="solid"
            color="primary"
            icon={<PlusCircle />}
          >
            {t.driversView.toolbar.create}
          </Button>
        </div>
      </Toolbar>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <span className="text-sm">
          <Badge color="primary" size="small">
            {selectedRows.length}
          </Badge>{' '}
          selected
        </span>
        <Separator hideDivider />
        <Button
          variant="solid"
          color="failure"
          icon={<Trash />}
          isDisabled={selectedRows.length < 1}
          onPress={handleDelete}
        >
          {t.driversView.toolbar.delete(selectedRows.length)}
        </Button>
      </FloatingToolbar>

      <Content>
        <Loading isLoading={isLoading}>
          <DataGrid<DriverRecord>
            columns={COLUMNS}
            dataSource={drivers}
            rowKey="id"
            aria-label={t.driversView.table.aria}
            selectionMode="multiple"
            onSelectionChange={handleSelectionChange}
            className="w-full text-sm"
            fullWidth
          />
        </Loading>
      </Content>

      <Footer>
        <Pagination
          limit={limit}
          offset={offset}
          total={data?.total ?? 0}
          onChange={handlePaginationChange}
        />
      </Footer>

      <DriverDialog
        driver={
          editDriver
            ? {
                id: editDriver.id,
                status: editDriver.status,
                firstName: editDriver.firstName,
                lastName: editDriver.lastName,
                licenseNumber: editDriver.licenseNumber,
                licenseExpiration: editDriver.licenseExpiration,
                phoneNumber: editDriver.phoneNumber,
                email: editDriver.email,
                address: editDriver.address,
                userId: editDriver.user?.id,
              }
            : undefined
        }
        isOpen={isDialogOpen}
        onClose={() => {
          setEditDriver(null);
          setIsDialogOpen(false);
        }}
        onSuccess={() => {
          setEditDriver(null);
          setIsDialogOpen(false);
          refetch();
        }}
      />
    </TransportationRootLayout>
  );
};
